import React from "react";
import { useSimulator } from "@/context/SimulatorContext";
import { Card } from "@/components/ui/card";
import { NumberField } from "@/components/simulator/NumberField";
import { fmtEUR, fmtNum, fmtPct } from "@/lib/format";
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { TrendingUp, Rocket, Wallet, Percent, CalendarCheck } from "lucide-react";

export const ProjectionsTab = () => {
  const { inputs, projection, updateProjection, updateCapex, summary, payback, irr } = useSimulator();
  const p = inputs.projection;
  const c = inputs.capex || { initial_investment: 0, discount_rate: 8 };

  const chartData = projection.map((row) => ({
    year: `Anno ${row.year}`,
    Ricavo: Math.round(row.revenue),
    OPEX: Math.round(row.opex),
    Margine: Math.round(row.margin),
    Cassa: Math.round(row.cumulativeCash),
  }));

  const finalYear = projection[projection.length - 1];

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-4">
        <div className="rounded-xl bg-violet-500/10 border border-violet-500/30 p-3">
          <TrendingUp className="h-7 w-7 text-violet-400" />
        </div>
        <div>
          <h2 className="font-display text-2xl font-bold text-slate-100">
            Proiezione a 5 Anni & Reinvestimento
          </h2>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl">
            Modello di crescita composta basata sull&apos;utile reinvestito in
            espansione impianti (rack + fotobioreattori). Include inflazione
            prezzi e costi, CAPEX iniziale, payback dinamico e IRR.
          </p>
        </div>
      </div>

      {/* CAPEX / Payback / IRR block */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="bg-slate-900/70 border-slate-800 p-5 space-y-4 lg:col-span-1">
          <div className="flex items-center gap-2 text-violet-400">
            <Wallet className="h-4 w-4" />
            <h3 className="font-display text-base font-semibold">Investimento iniziale</h3>
          </div>
          <NumberField
            label="CAPEX iniziale totale"
            value={c.initial_investment}
            onChange={(v) => updateCapex({ initial_investment: v })}
            min={0}
            max={3000000}
            step={5000}
            unit="€"
            testId="capex-initial-investment"
            sliderTheme="violet-slider"
            hint={`include nuovi lotti attivi: ${fmtEUR(summary.newLots?.total?.capex || 0)}`}
          />
          <NumberField
            label="Tasso di sconto (NPV/IRR)"
            value={c.discount_rate}
            onChange={(v) => updateCapex({ discount_rate: v })}
            min={0}
            max={25}
            step={0.5}
            unit="%"
            testId="capex-discount-rate"
            sliderTheme="violet-slider"
          />
        </Card>

        <Card data-testid="payback-card" className="bg-slate-900/70 border border-emerald-500/20 p-5">
          <div className="flex items-center gap-2 text-emerald-400 mb-2">
            <CalendarCheck className="h-4 w-4" />
            <h3 className="font-display text-base font-semibold">Payback dinamico</h3>
          </div>
          <div className="font-display font-extrabold text-3xl font-mono-tab text-emerald-300">
            {payback.reached && payback.payback_years !== null
              ? `${payback.payback_years.toFixed(2)} anni`
              : "> 5 anni"}
          </div>
          <div className="mt-1 text-xs text-slate-400">
            {payback.reached
              ? `Cumulato margine anno 5: ${fmtEUR(payback.cumulative_at_5y)}`
              : `Cumulato a 5 anni: ${fmtEUR(payback.cumulative_at_5y)} vs CAPEX ${fmtEUR(summary.totalCapex)}`}
          </div>
          <div className="mt-3 pt-3 border-t border-slate-800 text-[11px] text-slate-500 font-mono-tab">
            CAPEX totale: {fmtEUR(summary.totalCapex)}
          </div>
        </Card>

        <Card data-testid="irr-card" className="bg-slate-900/70 border border-sky-500/20 p-5">
          <div className="flex items-center gap-2 text-sky-400 mb-2">
            <Percent className="h-4 w-4" />
            <h3 className="font-display text-base font-semibold">IRR (5 anni)</h3>
          </div>
          <div className="font-display font-extrabold text-3xl font-mono-tab text-sky-300">
            {irr === null ? "N/D" : `${(irr * 100).toFixed(2)} %`}
          </div>
          <div className="mt-1 text-xs text-slate-400">
            {irr === null
              ? "Flussi non permettono un IRR calcolabile (tutti negativi o tutti positivi)."
              : irr > c.discount_rate / 100
              ? `Superiore alla soglia (${c.discount_rate}%) → progetto crea valore`
              : `Sotto la soglia richiesta (${c.discount_rate}%)`}
          </div>
          <div className="mt-3 pt-3 border-t border-slate-800 text-[11px] text-slate-500 font-mono-tab">
            ROI 5Y semplice: {fmtPct(summary.roi5y, 1)}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-slate-900/70 border-slate-800 p-6 space-y-5">
          <div className="flex items-center gap-2 text-violet-400">
            <Rocket className="h-4 w-4" />
            <h3 className="font-display text-lg font-semibold">Leve di crescita</h3>
          </div>

          <NumberField
            label="% Reinvestimento utili"
            value={p.reinvestment_pct}
            onChange={(v) => updateProjection({ reinvestment_pct: v })}
            min={0}
            max={100}
            step={5}
            unit="%"
            testId="reinvestment-pct"
            sliderTheme="violet-slider"
            hint="quota dell'utile operativo reinvestita in espansione"
          />

          <NumberField
            label="CapEx per +1% capacità produttiva"
            value={p.expansion_capex_per_pct}
            onChange={(v) => updateProjection({ expansion_capex_per_pct: v })}
            min={5000}
            max={200000}
            step={1000}
            unit="€ / +1%"
            testId="expansion-capex"
            sliderTheme="violet-slider"
          />

          <NumberField
            label="Inflazione ricavi annua"
            value={p.revenue_inflation_pct}
            onChange={(v) => updateProjection({ revenue_inflation_pct: v })}
            min={0}
            max={15}
            step={0.5}
            unit="%"
            testId="revenue-inflation"
            sliderTheme="algae-slider"
          />

          <NumberField
            label="Inflazione OPEX annua"
            value={p.opex_inflation_pct}
            onChange={(v) => updateProjection({ opex_inflation_pct: v })}
            min={0}
            max={15}
            step={0.5}
            unit="%"
            testId="opex-inflation"
            sliderTheme="saffron-slider"
          />
        </Card>

        <Card className="bg-slate-900/70 border-slate-800 p-6">
          <h3 className="font-display text-lg font-semibold text-slate-100 mb-4">
            Proiezione 5 Anni
          </h3>
          <div data-testid="projections-5yr-chart-container" className="h-[340px]">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData}>
                <defs>
                  <linearGradient id="cashGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.55} />
                    <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                <XAxis dataKey="year" stroke="#64748b" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                <YAxis
                  stroke="#64748b"
                  tick={{ fontSize: 10, fill: "#94a3b8" }}
                  tickFormatter={(v) => fmtEUR(v, { compact: true })}
                />
                <Tooltip
                  formatter={(v) => fmtEUR(v)}
                  contentStyle={{ background: "#0b0f19", border: "1px solid #1f2937", borderRadius: 8 }}
                />
                <Legend wrapperStyle={{ fontSize: 11, color: "#94a3b8" }} />
                <Bar dataKey="Ricavo" fill="#10b981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="OPEX" fill="#f43f5e" radius={[4, 4, 0, 0]} />
                <Line type="monotone" dataKey="Margine" stroke="#3b82f6" strokeWidth={2.5} dot={{ r: 4 }} />
                <Area type="monotone" dataKey="Cassa" stroke="#8b5cf6" strokeWidth={2} fill="url(#cashGrad)" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card className="bg-slate-900/70 border-slate-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-slate-800 bg-slate-950/40">
                <Th>Anno</Th>
                <Th>Capacità</Th>
                <Th className="text-right">Ricavo</Th>
                <Th className="text-right">OPEX</Th>
                <Th className="text-right">Risparmio Energia</Th>
                <Th className="text-right">CAPEX Anno</Th>
                <Th className="text-right">EBITDA</Th>
                <Th className="text-right">Margin</Th>
                <Th className="text-right">Cash Cumulato</Th>
              </tr>
            </thead>
            <tbody>
              {projection.map((row) => (
                <tr
                  key={row.year}
                  data-testid={`projection-row-${row.year}`}
                  className="border-b border-slate-800/60 hover:bg-slate-800/20 transition-colors"
                >
                  <Td className="font-semibold text-slate-100">Anno {row.year}</Td>
                  <Td className="font-mono-tab text-slate-300">{fmtNum(row.capacity, 3)}×</Td>
                  <Td className="text-right font-mono-tab text-emerald-400">{fmtEUR(row.revenue)}</Td>
                  <Td className="text-right font-mono-tab text-rose-400">{fmtEUR(row.opex)}</Td>
                  <Td className="text-right font-mono-tab text-emerald-300">{row.energy_saving_year ? fmtEUR(row.energy_saving_year) : "—"}</Td>
                  <Td className="text-right font-mono-tab text-violet-400">{row.capex_year ? fmtEUR(row.capex_year) : "—"}</Td>
                  <Td className={`text-right font-mono-tab ${row.margin >= 0 ? "text-sky-400" : "text-rose-500"}`}>
                    {fmtEUR(row.margin)}
                  </Td>
                  <Td className={`text-right font-mono-tab ${row.margin >= 0 ? "text-sky-300" : "text-rose-400"}`}>
                    {fmtPct(row.ebitdaMargin || 0, 1)}
                  </Td>
                  <Td className={`text-right font-mono-tab font-semibold ${row.cumulativeCash >= 0 ? "text-emerald-300" : "text-rose-400"}`}>
                    {fmtEUR(row.cumulativeCash)}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {finalYear && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card data-testid="final-year-revenue" className="bg-emerald-500/10 border-emerald-500/30 p-5 border">
            <div className="text-xs font-semibold uppercase tracking-wider text-emerald-300">
              Ricavo Anno 5
            </div>
            <div className="mt-2 font-display font-extrabold text-2xl text-emerald-300 font-mono-tab">
              {fmtEUR(finalYear.revenue)}
            </div>
            <div className="mt-1 text-xs text-slate-400">
              +{fmtPct(((finalYear.revenue / projection[0].revenue - 1) * 100))} vs anno 1
            </div>
          </Card>
          <Card data-testid="final-year-capacity" className="bg-violet-500/10 border-violet-500/30 p-5 border">
            <div className="text-xs font-semibold uppercase tracking-wider text-violet-300">
              Capacità Anno 5
            </div>
            <div className="mt-2 font-display font-extrabold text-2xl text-violet-300 font-mono-tab">
              {fmtNum(finalYear.capacity, 3)}×
            </div>
            <div className="mt-1 text-xs text-slate-400">rispetto alla base attuale</div>
          </Card>
          <Card data-testid="final-year-cash" className="bg-sky-500/10 border-sky-500/30 p-5 border">
            <div className="text-xs font-semibold uppercase tracking-wider text-sky-300">
              Cassa Cumulata a 5 Anni
            </div>
            <div className="mt-2 font-display font-extrabold text-2xl text-sky-300 font-mono-tab">
              {fmtEUR(finalYear.cumulativeCash)}
            </div>
            <div className="mt-1 text-xs text-slate-400">al netto di reinvestimenti</div>
          </Card>
        </div>
      )}
    </div>
  );
};

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-400 ${className}`}>
    {children}
  </th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 ${className}`}>{children}</td>
);
